"use client";

import React, { useCallback, useRef } from "react";
import styled from "styled-components";
import { useStoredState } from "@thenounproject/lingo-core";
import { SubNavContainer } from "./SubNav";
import { SUB_NAV_WIDTH, SUB_NAV_MIN_WIDTH, SUB_NAV_MAX_WIDTH } from "../../constants/layouts";

const Wrapper = styled.div`
  position: relative;
  display: flex;
  height: 100%;
`;

const DragHandle = styled.div`
  position: absolute;
  top: 0;
  right: -1px;
  width: 3px;
  height: 100%;
  cursor: col-resize;
  z-index: 1;
  opacity: 0;
  transition: ease 0.2s;
  background: rgba(0, 0, 0, 0.08);
  &:hover,
  &:active {
    opacity: 0.5;
  }
`;

type Props = {
  children: React.ReactNode;
  saveId: string;
};

export default function ResizableSubNav({ children, saveId }: Props) {
  const [width, setWidth] = useStoredState(saveId, SUB_NAV_WIDTH);
  const widthRef = useRef(width);
  const containerRef = useRef<HTMLDivElement>(null);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const startX = e.clientX,
        startWidth = widthRef.current;

      const onPointerMove = (ev: PointerEvent) => {
        const newWidth = Math.min(
          SUB_NAV_MAX_WIDTH,
          Math.max(SUB_NAV_MIN_WIDTH, startWidth + (ev.clientX - startX))
        );
        widthRef.current = newWidth;
        if (containerRef.current) {
          containerRef.current.style.width = `${newWidth}px`;
          containerRef.current.style.flex = `0 0 ${newWidth}px`;
        }
      };

      const onPointerUp = () => {
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        setWidth(widthRef.current);
      };

      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
    },
    [setWidth]
  );

  return (
    <Wrapper>
      <SubNavContainer ref={containerRef} style={{ width: `${width}px`, flex: `0 0 ${width}px` }}>
        {children}
      </SubNavContainer>
      <DragHandle onPointerDown={onPointerDown} />
    </Wrapper>
  );
}
